import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { AlertBanner, Modal, TextInput, useLocale } from '@ministry/ui/internal';

import { createOrgUnit, renameOrgUnit, archiveOrgUnit, type OrgUnit, type OrgUnitKind } from '../../lib/admin-org.js';
import { ApiError, NetworkError } from '../../lib/api-client.js';
import { orgUnitKindLabel } from '../../lib/admin-lifecycle.js';
import { genericFieldErrorMessage } from '../../lib/field-errors.js';
import { useT } from '../../i18n/strings.js';

/** `ministry -> division -> section`, and `section` has no further children (`ORG_UNIT_KINDS`). */
export const CHILD_KIND: Record<OrgUnitKind, OrgUnitKind | null> = {
  ministry: 'division',
  division: 'section',
  section: null,
};

export interface CreateOrgUnitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parent: { id: string; kind: OrgUnitKind; label: string } | null;
  onSuccess: (unit: OrgUnit) => void;
}

/** SCREEN_SPECS.md I20 — "modal for add/edit/rename a node." */
export function CreateOrgUnitModal({ open, onOpenChange, parent, onSuccess }: CreateOrgUnitModalProps): React.JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const kind: OrgUnitKind = parent ? (CHILD_KIND[parent.kind] as OrgUnitKind) : 'ministry';
  const [code, setCode] = React.useState('');
  const [nameNe, setNameNe] = React.useState('');
  const [nameEn, setNameEn] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setCode('');
      setNameNe('');
      setNameEn('');
      setTouched(false);
    }
  }, [open]);

  const mutation = useMutation({
    mutationFn: () =>
      createOrgUnit({ parentId: parent?.id ?? null, kind, code: code.trim(), nameNe: nameNe.trim(), nameEn: nameEn.trim() }),
    onSuccess: (unit) => {
      onOpenChange(false);
      onSuccess(unit);
    },
  });

  const codeError = touched && code.trim().length === 0 ? t('admin.org.create.code.error') : undefined;
  const nameNeError = touched && nameNe.trim().length === 0 ? t('admin.org.create.nameNe.error') : undefined;
  const nameEnError = touched && nameEn.trim().length === 0 ? t('admin.org.create.nameEn.error') : undefined;

  const fieldErrorFor = (field: string): string | undefined => {
    if (!(mutation.error instanceof ApiError)) return undefined;
    const match = mutation.error.fieldErrors.find((fe) => fe.field === field);
    if (!match) return undefined;
    return genericFieldErrorMessage(t, match.code, match.params);
  };

  const banner = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof NetworkError) return <AlertBanner variant="critical" title={t('admin.error.network')} />;
    if (mutation.error instanceof ApiError) {
      if (mutation.error.code === 'CONFLICT_DUPLICATE') {
        return <AlertBanner variant="critical" title={t('admin.org.create.error.duplicateCode')} />;
      }
      if (mutation.error.fieldErrors.some((fe) => fe.field === 'parentId')) {
        return <AlertBanner variant="critical" title={t('admin.org.create.error.badParent')} />;
      }
      return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
    }
    return null;
  })();

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('admin.org.create.title', { kind: orgUnitKindLabel(kind, locale) })}
      description={parent ? t('admin.org.create.descriptionWithParent', { parent: parent.label }) : t('admin.org.create.descriptionRoot')}
      confirmLabel={mutation.isPending ? t('admin.org.create.submitting') : t('admin.org.create.submit')}
      cancelLabel={t('admin.common.cancel')}
      loading={mutation.isPending}
      onConfirm={() => {
        setTouched(true);
        if (code.trim() === '' || nameNe.trim() === '' || nameEn.trim() === '') return;
        mutation.mutate();
      }}
    >
      <div className="flex flex-col gap-4">
        {banner}
        <TextInput
          id="admin-org-create-code"
          label={t('admin.org.create.code.label')}
          required
          value={code}
          onChange={(event) => setCode(event.target.value)}
          errorText={codeError ?? fieldErrorFor('code')}
        />
        <TextInput
          id="admin-org-create-namene"
          label={t('admin.org.create.nameNe.label')}
          required
          value={nameNe}
          onChange={(event) => setNameNe(event.target.value)}
          errorText={nameNeError ?? fieldErrorFor('nameNe')}
        />
        <TextInput
          id="admin-org-create-nameen"
          label={t('admin.org.create.nameEn.label')}
          required
          value={nameEn}
          onChange={(event) => setNameEn(event.target.value)}
          errorText={nameEnError ?? fieldErrorFor('nameEn')}
        />
      </div>
    </Modal>
  );
}

export interface RenameOrgUnitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unit: OrgUnit | null;
  onSuccess: (unit: OrgUnit) => void;
}

export function RenameOrgUnitModal({ open, onOpenChange, unit, onSuccess }: RenameOrgUnitModalProps): React.JSX.Element | null {
  const t = useT();
  const { locale } = useLocale();
  const [code, setCode] = React.useState('');
  const [nameNe, setNameNe] = React.useState('');
  const [nameEn, setNameEn] = React.useState('');

  React.useEffect(() => {
    if (open && unit) {
      setCode(unit.code);
      setNameNe(unit.nameNe);
      setNameEn(unit.nameEn);
    }
  }, [open, unit]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!unit) throw new Error('No unit selected.');
      return renameOrgUnit(unit.id, { code: code.trim(), nameNe: nameNe.trim(), nameEn: nameEn.trim(), expectedVersion: unit.version });
    },
    onSuccess: (updated) => {
      onOpenChange(false);
      onSuccess(updated);
    },
  });

  if (!unit) return null;

  const isConflict = mutation.error instanceof ApiError && mutation.error.code === 'CONFLICT_STATE';

  const fieldErrorFor = (field: string): string | undefined => {
    if (!(mutation.error instanceof ApiError)) return undefined;
    const match = mutation.error.fieldErrors.find((fe) => fe.field === field);
    if (!match) return undefined;
    return genericFieldErrorMessage(t, match.code, match.params);
  };

  const banner = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof NetworkError) return <AlertBanner variant="critical" title={t('admin.error.network')} />;
    if (isConflict) return <AlertBanner variant="critical" title={t('admin.org.rename.error.conflict')} />;
    if (mutation.error instanceof ApiError && mutation.error.code === 'CONFLICT_DUPLICATE') {
      return <AlertBanner variant="critical" title={t('admin.org.create.error.duplicateCode')} />;
    }
    return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
  })();

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('admin.org.rename.title', { name: locale === 'ne' ? unit.nameNe : unit.nameEn })}
      confirmLabel={mutation.isPending ? t('admin.org.rename.submitting') : t('admin.org.rename.submit')}
      cancelLabel={t('admin.common.cancel')}
      loading={mutation.isPending}
      onConfirm={() => mutation.mutate()}
    >
      <div className="flex flex-col gap-4">
        {banner}
        <TextInput
          id="admin-org-rename-code"
          label={t('admin.org.create.code.label')}
          required
          value={code}
          onChange={(event) => setCode(event.target.value)}
          errorText={fieldErrorFor('code')}
        />
        <TextInput
          id="admin-org-rename-namene"
          label={t('admin.org.create.nameNe.label')}
          required
          value={nameNe}
          onChange={(event) => setNameNe(event.target.value)}
          errorText={fieldErrorFor('nameNe')}
        />
        <TextInput
          id="admin-org-rename-nameen"
          label={t('admin.org.create.nameEn.label')}
          required
          value={nameEn}
          onChange={(event) => setNameEn(event.target.value)}
          errorText={fieldErrorFor('nameEn')}
        />
      </div>
    </Modal>
  );
}

export interface ArchiveOrgUnitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  unit: OrgUnit | null;
  onSuccess: (unit: OrgUnit) => void;
}

/**
 * SCREEN_SPECS.md I20: "deleting/renaming a node that has staff or files attached
 * requires an explicit confirmation naming the count affected." `409
 * ORG_UNIT_NOT_EMPTY` carries exactly those counts in `error.details`
 * (API_CONTRACT §13.10 amendment 3) — rendered here, not paraphrased from the English
 * `error.message`.
 */
export function ArchiveOrgUnitModal({ open, onOpenChange, unit, onSuccess }: ArchiveOrgUnitModalProps): React.JSX.Element | null {
  const t = useT();
  const { locale } = useLocale();
  const mutation = useMutation({
    mutationFn: () => {
      if (!unit) throw new Error('No unit selected.');
      return archiveOrgUnit(unit.id);
    },
    onSuccess: (archived) => {
      onOpenChange(false);
      onSuccess(archived);
    },
  });

  const { reset: resetMutation } = mutation;
  React.useEffect(() => {
    if (!open) resetMutation();
  }, [open, resetMutation]);

  if (!unit) return null;

  const notEmpty = mutation.error instanceof ApiError && mutation.error.code === 'ORG_UNIT_NOT_EMPTY';
  const banner = (() => {
    if (!mutation.isError) return null;
    if (mutation.error instanceof NetworkError) return <AlertBanner variant="critical" title={t('admin.error.network')} />;
    if (notEmpty && mutation.error instanceof ApiError) {
      const details = mutation.error.details ?? {};
      return (
        <AlertBanner variant="critical" title={t('admin.org.archive.error.notEmpty.title')}>
          <p>
            {t('admin.org.archive.error.notEmpty.body', {
              liveFiles: typeof details.liveFiles === 'number' ? details.liveFiles : 0,
              activeStaff: typeof details.activeStaff === 'number' ? details.activeStaff : 0,
              liveChildren: typeof details.liveChildren === 'number' ? details.liveChildren : 0,
            })}
          </p>
        </AlertBanner>
      );
    }
    if (mutation.error instanceof ApiError && mutation.error.code === 'NOT_FOUND') {
      return <AlertBanner variant="critical" title={t('admin.org.archive.error.notFound')} />;
    }
    return <AlertBanner variant="critical" title={t('admin.error.generic')} />;
  })();

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (mutation.isPending) return;
        onOpenChange(next);
      }}
      title={t('admin.org.archive.title', { name: locale === 'ne' ? unit.nameNe : unit.nameEn })}
      description={t('admin.org.archive.description', { name: locale === 'ne' ? unit.nameNe : unit.nameEn })}
      confirmLabel={mutation.isPending ? t('admin.org.archive.submitting') : t('admin.org.archive.submit')}
      cancelLabel={t('admin.common.cancel')}
      variant="destructive"
      loading={mutation.isPending}
      onConfirm={() => mutation.mutate()}
    >
      {banner}
    </Modal>
  );
}
